/**
 * 图片上传服务
 * 将图片上传到 Cloudflare R2 存储
 */

import { supabase } from './supabase';

// Supabase Edge Function URL
const getFunctionUrl = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  return `${supabaseUrl}/functions/v1/upload-image`;
};

// 上传结果类型
export interface UploadResult {
  success: boolean;
  urls: string[];
  count: number;
  error?: string;
}

/**
 * 压缩图片
 * @param file 图片文件
 * @param maxWidth 最大宽度
 * @param quality 压缩质量 (0-1)
 * @returns base64 编码的图片
 */
async function compressImage(
  file: File,
  maxWidth: number = 1200,
  quality: number = 0.8
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // 按比例缩放
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        // 转换为 base64
        const base64 = canvas.toDataURL('image/jpeg', quality);
        resolve(base64);
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * 批量上传图片到 R2
 * @param files 图片文件数组
 * @param userId 用户ID
 * @returns 上传结果
 */
export async function uploadImages(
  files: File[],
  userId: string
): Promise<UploadResult> {
  if (!files || files.length === 0) {
    return { success: false, urls: [], count: 0, error: '没有选择图片' };
  }

  if (!userId) {
    return { success: false, urls: [], count: 0, error: '用户未登录' };
  }

  try {
    // 压缩并转换为 base64
    const base64Images: string[] = [];
    for (const file of files) {
      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        console.warn(`跳过非图片文件: ${file.name}`);
        continue;
      }

      // 检查文件大小 (最大 10MB)
      if (file.size > 10 * 1024 * 1024) {
        console.warn(`跳过过大文件: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        continue;
      }

      // 压缩图片
      const compressed = await compressImage(file);
      base64Images.push(compressed);
    }

    if (base64Images.length === 0) {
      return { success: false, urls: [], count: 0, error: '没有有效的图片' };
    }

    // 获取用户的 session token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    // 调用 Edge Function 上传
    const response = await fetch(getFunctionUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        images: base64Images,
        userId,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(result.error || '上传失败');
    }

    return {
      success: true,
      urls: result.urls,
      count: result.count,
    };
  } catch (error) {
    console.error('图片上传失败:', error);
    return {
      success: false,
      urls: [],
      count: 0,
      error: error instanceof Error ? error.message : '上传失败',
    };
  }
}

/**
 * 上传单张图片
 * @param file 图片文件
 * @param userId 用户ID
 * @returns 图片 URL 或 null
 */
export async function uploadSingleImage(
  file: File,
  userId: string
): Promise<string | null> {
  const result = await uploadImages([file], userId);
  return result.success && result.urls.length > 0 ? result.urls[0] : null;
}